import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { haversineKm, getMassSubLabel } from '../../utils/priceCalculator';
import { massLabel, activeMassTypes, findMassType, useMassCatalog } from '../../utils/massCatalog';
import { placeName, fullAddress, groupOrdersByDay, timeLabel, toDate, kmLabel, dayLabel } from '../../utils/orderCardFormat';
import { matchesAfter, bestAnchorFor, bestMatchSameDay } from '../../utils/routeMatching';
import MassIcon from './MassIcon';
import ExpandedOrder from './ExpandedOrder';
import InvoiceBadge from '../common/InvoiceBadge';
import { orderTitle } from '../../utils/orderTitle';
import { orderHourlyText } from '../../utils/orderHourly';
import { priceKindLabel } from '../../utils/orderPricing';
import { COLORS, SHADOWS, FONTS } from '../../styles/tokens';
import { useOrdererProfile } from '../../hooks/useOrdererProfile';
import { computeOrdererBadge, ORDERER_BADGE_CONFIG } from '../../utils/ordererBadge';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { TruckAnimation } from '../common/truck';
import { OrderSupplierLogo } from '../common/SupplierLogo';

const STATUS_TEXT = {
  matched:           'Klar for henting',
  in_progress:       'Under transport',
  delivered_pending: 'Venter på bekreftelse',
};

const NO_ORDERS = [];

// tab ('mine' | 'new') brukes bare på mobil, der arket viser én fane om gangen.
// På desktop vises pågående og nye oppdrag sammen som før.
export default function OrderSidebar({ orders, loading, error, selectedOrder, onSelect, driverLocation, driverBids, mobile, tab = 'new', activeOrders = NO_ORDERS, selectedActiveOrder, onActiveSelect, onOpenActive }) {
  useMassCatalog();
  const [massFilter, setMassFilter] = useState('');
  // Filteret gjelder bare aktive typer, så UI og filtrering er enige selv om admin skrur av typen underveis.
  const effectiveMassFilter = findMassType(massFilter)?.active ? massFilter : '';
  const [maxKm, setMaxKm]           = useState(50);
  const [filtersOpen,    setFiltersOpen]    = useState(false);
  const [priceType,      setPriceType]      = useState('all');      // 'all' | 'fixed' | 'bid'
  const [deliveryFilter, setDeliveryFilter] = useState('all');      // 'all' | 'today' | '3days' | 'week'
  const [hideEquipment,  setHideEquipment]  = useState(false);
  const [sortBy,         setSortBy]         = useState('distance'); // 'distance' | 'date' | 'price'

  const { currentUser } = useAuth();

  const activeFilterCount = [
    effectiveMassFilter !== '',
    maxKm !== 50,
    priceType !== 'all',
    deliveryFilter !== 'all',
    hideEquipment,
    sortBy !== 'distance',
  ].filter(Boolean).length;

  // Load saved preferences on mount
  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'users', currentUser.uid)).then((snap) => {
      const prefs = snap.data()?.preferences;
      if (!prefs) return;
      if (prefs.massTypes?.length === 1 && findMassType(prefs.massTypes[0])?.active === true) setMassFilter(prefs.massTypes[0]);
      if (prefs.maxKm != null) setMaxKm(prefs.maxKm);
    });
  }, [currentUser]);

  // Persist massTypes when filter changes
  useEffect(() => {
    if (!currentUser) return;
    updateDoc(doc(db, 'users', currentUser.uid), {
      'preferences.massTypes': massFilter ? [massFilter] : [],
    }).catch(() => {});
  }, [massFilter, currentUser]);

  // Persist maxKm when radius changes
  useEffect(() => {
    if (!currentUser) return;
    updateDoc(doc(db, 'users', currentUser.uid), {
      'preferences.maxKm': maxKm,
    }).catch(() => {});
  }, [maxKm, currentUser]);

  const filtered = useMemo(() => {
    const now = new Date();
    return orders.filter(order => {
      // Massetype
      if (effectiveMassFilter && order.massType !== effectiveMassFilter) return false;
      // Avstand
      if (driverLocation && order.pickupAddress?.lat) {
        const km = haversineKm(
          driverLocation.lat, driverLocation.lng,
          order.pickupAddress.lat, order.pickupAddress.lng
        );
        if (km > maxKm) return false;
      }
      // Pristype
      if (priceType === 'fixed' && !(order.fixedPrice != null && order.fixedPrice > 0)) return false;
      if (priceType === 'bid'   && (order.fixedPrice != null && order.fixedPrice > 0))  return false;
      // Leveringsdato
      if (deliveryFilter !== 'all') {
        const scheduledDate = order.scheduledAt?.toDate?.()
          ?? (order.scheduledAt ? new Date(order.scheduledAt) : null);
        if (!scheduledDate) return false;
        if (deliveryFilter === 'today') {
          if (scheduledDate.toLocaleDateString('nb-NO') !== now.toLocaleDateString('nb-NO')) return false;
        } else if (deliveryFilter === '3days') {
          if (scheduledDate > new Date(now.getTime() + 72 * 60 * 60 * 1000)) return false;
        } else if (deliveryFilter === 'week') {
          if (scheduledDate > new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)) return false;
        }
      }
      // Lasteutstyr
      if (hideEquipment && order.requiresLoadingEquipment) return false;
      return true;
    }).sort((a, b) => {
      if (sortBy === 'date') {
        const dA = a.scheduledAt?.toDate?.()?.getTime() ?? Infinity;
        const dB = b.scheduledAt?.toDate?.()?.getTime() ?? Infinity;
        return dA - dB;
      }
      if (sortBy === 'price') {
        const pA = a.fixedPrice ?? -1;
        const pB = b.fixedPrice ?? -1;
        return pB - pA;
      }
      // default: distance
      if (!driverLocation) return 0;
      const distA = a.pickupAddress?.lat
        ? haversineKm(driverLocation.lat, driverLocation.lng, a.pickupAddress.lat, a.pickupAddress.lng)
        : 999;
      const distB = b.pickupAddress?.lat
        ? haversineKm(driverLocation.lat, driverLocation.lng, b.pickupAddress.lat, b.pickupAddress.lng)
        : 999;
      return distA - distB;
    });
  }, [orders, effectiveMassFilter, maxKm, priceType, deliveryFilter, hideEquipment, sortBy, driverLocation]);

  const anchorByOrder = useMemo(() => bestAnchorFor(filtered, activeOrders), [filtered, activeOrders]);

  // Pågående oppdrag gruppert per dag, likt lista med nye oppdrag (mobilfane og desktop)
  const renderActiveGroups = () => groupOrdersByDay(activeOrders).map(group => (
    <React.Fragment key={group.key}>
      <div style={styles.dayHeader}>{group.label}</div>
      {group.orders.map(order => (
        <ActiveOrderCard
          key={order.id}
          order={order}
          selected={selectedActiveOrder?.id === order.id}
          onSelect={onActiveSelect}
          openOrders={filtered}
          onPickMatch={onSelect}
          onOpen={onOpenActive}
        />
      ))}
    </React.Fragment>
  ));

  // Mobil, fanen «Mine oppdrag»: pågående oppdrag
  if (mobile && tab === 'mine') {
    return (
      <div style={{ ...styles.sidebar, ...styles.sidebarMobile }}>
        <div style={styles.list}>
          {activeOrders.length === 0 ? (
            <div style={styles.emptyState}>
              <TruckAnimation variant="empty" size={110} decorative style={{ marginBottom: 8 }} />
              <p style={{ fontWeight: 600, margin: '0 0 4px' }}>Ingen pågående oppdrag</p>
              <p style={{ fontSize: 13, color: COLORS.textLight, margin: 0 }}>
                Oppdrag du får tildelt vises her
              </p>
            </div>
          ) : renderActiveGroups()}
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.sidebar, ...(mobile ? styles.sidebarMobile : {}) }}>
      {/* Filter- og sorteringspanel */}
      <div style={{ ...styles.filterBox, ...(mobile ? styles.filterBoxMobile : {}) }}>
        {/* Header */}
        <button
          onClick={() => setFiltersOpen(o => !o)}
          style={styles.filterHeader}
        >
          {mobile ? (
            <>
              <span style={styles.sheetTitle}>
                {filtered.length} oppdrag i nærheten
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {activeFilterCount > 0 && (
                  <span style={styles.filterBadge}>{activeFilterCount}</span>
                )}
                <span style={{ fontSize: 13, fontWeight: 600, color: filtersOpen ? COLORS.primary : COLORS.textDark }}>
                  Filter
                </span>
                <span style={{ fontSize: 12, color: COLORS.textMid }}>{filtersOpen ? '▲' : '▼'}</span>
              </div>
            </>
          ) : (
            <>
              <span style={{ fontWeight: 600, fontSize: 13, color: COLORS.primary }}>
                ⚙ Filter &amp; sortering
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {activeFilterCount > 0 && (
                  <span style={styles.filterBadge}>{activeFilterCount}</span>
                )}
                <span style={{ fontSize: 12, color: COLORS.textMid }}>{filtersOpen ? '▲' : '▼'}</span>
              </div>
            </>
          )}
        </button>

        {/* Ekspandert innhold */}
        {filtersOpen && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Massetype */}
            <select
              className="tippi-input"
              style={styles.select}
              value={effectiveMassFilter}
              onChange={e => setMassFilter(e.target.value)}
            >
              {[{ value: '', label: 'Alle massetyper' }, ...activeMassTypes().map(t => ({ value: t.key, label: t.label }))]
                .map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {/* Avstand */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <label style={styles.filterLabel}>Maks avstand</label>
                <strong style={{ fontSize: 13, color: COLORS.primary }}>{maxKm} km</strong>
              </div>
              <input
                type="range" min={5} max={200} step={5}
                value={maxKm}
                onChange={e => setMaxKm(Number(e.target.value))}
                style={{ width: '100%', accentColor: COLORS.primary }}
              />
            </div>

            {/* Pristype */}
            <div>
              <label style={styles.filterLabel}>Pristype</label>
              <div style={styles.toggleGroup}>
                {[
                  { value: 'all',   label: 'Alle' },
                  { value: 'fixed', label: 'Fastpris' },
                  { value: 'bid',   label: 'Anbud' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setPriceType(opt.value)}
                    style={{ ...styles.toggleBtn, ...(priceType === opt.value ? styles.toggleBtnActive : {}) }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Levering */}
            <div>
              <label style={styles.filterLabel}>Levering</label>
              <div style={styles.toggleGroup}>
                {[
                  { value: 'all',    label: 'Alle' },
                  { value: 'today',  label: 'I dag' },
                  { value: '3days',  label: '3 dager' },
                  { value: 'week',   label: 'Neste 7 dager' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setDeliveryFilter(opt.value)}
                    style={{ ...styles.toggleBtn, ...(deliveryFilter === opt.value ? styles.toggleBtnActive : {}) }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Lasteutstyr */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={hideEquipment}
                onChange={e => setHideEquipment(e.target.checked)}
                style={{ accentColor: COLORS.primary, width: 15, height: 15 }}
              />
              <span>Skjul oppdrag med lasteutstyrkrav</span>
            </label>

            {/* Sorter etter */}
            <div>
              <label style={styles.filterLabel}>Sorter etter</label>
              <select
                className="tippi-input"
                style={styles.select}
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
              >
                <option value="distance">Nærmest</option>
                <option value="date">Tidligst levering</option>
                <option value="price">Høyest pris</option>
              </select>
            </div>

            {/* Nullstill */}
            {activeFilterCount > 0 && (
              <button
                onClick={() => {
                  setMassFilter('');
                  setMaxKm(50);
                  setPriceType('all');
                  setDeliveryFilter('all');
                  setHideEquipment(false);
                  setSortBy('distance');
                }}
                style={styles.resetBtn}
              >
                Nullstill
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pågående oppdrag — desktop (under filter) */}
      {!mobile && activeOrders.length > 0 && (
        <div style={styles.activeSection}>
          <span style={styles.sectionHeader}>PÅGÅENDE OPPDRAG</span>
          {renderActiveGroups()}
        </div>
      )}

      {/* Order list */}
      <div style={{ ...styles.list, ...(mobile ? styles.listMobile : {}) }}>
        {loading && <SkeletonCards />}
        {error  && <p style={styles.stateErr}>Feil ved henting av oppdrag</p>}

        {!loading && !error && filtered.length === 0 && (
          <div style={styles.emptyState}>
            <TruckAnimation variant="empty" size={110} decorative style={{ marginBottom: 8 }} />
            <p style={{ fontWeight: 600, margin: '0 0 4px' }}>Ingen åpne oppdrag</p>
            <p style={{ fontSize: 13, color: COLORS.textLight, margin: 0 }}>
              {orders.length > 0 ? 'Prøv å endre filtrene' : 'Nye oppdrag vises her i sanntid'}
            </p>
          </div>
        )}

        {!loading && !error && groupOrdersByDay(filtered).map(group => (
          <React.Fragment key={group.key}>
            {!mobile && <div style={styles.dayHeader}>{group.label}</div>}
            {group.orders.map(order => (
              <OrderCard
                key={order.id}
                mobile={mobile}
                order={order}
                driverLocation={driverLocation}
                selected={selectedOrder?.id === order.id}
                onClick={() => onSelect(selectedOrder?.id === order.id ? null : order)}
                existingBid={driverBids?.get(order.id) ?? null}
                openOrders={filtered}
                onPickMatch={onSelect}
                anchorMatch={anchorByOrder.get(order.id) ?? null}
                onTaken={onOpenActive}
              />
            ))}
          </React.Fragment>
        ))}
      </div>

      {/* På mobil står antallet allerede i filterlinjen */}
      {!mobile && (
        <div style={styles.count}>
          {filtered.length} {filtered.length === 1 ? 'oppdrag' : 'oppdrag'} tilgjengelig
        </div>
      )}
    </div>
  );
}

// Lukket: ikon, «Kjeller → Skui», last og klokkeslett, pris. Åpnet: full rute og detaljer.
// Dagen står i overskriften over kortet, så kortet viser bare klokkeslett.
function OrderCard({ mobile, order, driverLocation, selected, onClick, existingBid, openOrders = NO_ORDERS, onPickMatch, anchorMatch, onTaken }) {
  const ref = useRef(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [selected]);

  const scheduledDate = toDate(order.scheduledAt);
  const time      = timeLabel(scheduledDate);
  const routeKm   = order.pickupAddress?.lat != null && order.deliveryAddress?.lat != null
    ? haversineKm(order.pickupAddress.lat, order.pickupAddress.lng, order.deliveryAddress.lat, order.deliveryAddress.lng)
    : null;
  const matches = useMemo(
    () => (selected ? matchesAfter(order, openOrders) : []),
    [selected, order, openOrders]
  );

  return (
    <div
      ref={ref}
      className={mobile ? 'tippi-order-card tippi-order-card--flat' : 'tippi-order-card'}
      data-selected={selected ? 'true' : undefined}
      onClick={onClick}
      style={{
        ...styles.card,
        ...(mobile ? styles.cardFlat : {}),
        ...(selected && !mobile ? styles.cardSelected : {}),
      }}
    >
      {/* ── Alltid synlig ── */}
      <div style={styles.cardRow}>
        <MassIcon massType={order.massType} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.route}>
            {orderTitle(order)}
          </div>
          <div style={styles.cardSub}>
            <OrderSupplierLogo order={order} />{placeName(order.pickupAddress)} → {placeName(order.deliveryAddress)}
          </div>
          <div style={styles.cardMono}>
            {[routeKm != null && kmLabel(routeKm), scheduledDate && `${dayLabel(scheduledDate)} ${time}`].filter(Boolean).join(' · ')}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {existingBid?.amount != null
            ? (
              <>
                <div style={{ ...styles.price, color: COLORS.warningText }}>{existingBid.amount.toLocaleString('nb-NO')}</div>
                <div style={styles.myBidLabel}>Ditt bud</div>
              </>
            )
            : order.fixedPrice
              ? <div style={styles.price}>{order.fixedPrice.toLocaleString('nb-NO')}</div>
              : <div style={styles.bidTag}>Bud</div>
          }
          {existingBid?.amount == null && (
            <div style={styles.kindTag}>{priceKindLabel(order)}</div>
          )}
          <InvoiceBadge order={order} style={{ marginTop: 4 }} />
        </div>
      </div>

      {anchorMatch && (
        <div style={styles.matchBadge}>
          ↪ Passer etter {placeName(anchorMatch.anchor.pickupAddress)} → {placeName(anchorMatch.anchor.deliveryAddress)}
        </div>
      )}

      {/* ── Åpnet: oppdragsgiver, adresser, bud/fastpris direkte i kortet og «Se alle detaljer» ── */}
      {selected && (
        <>
          <ExpandedOrder order={order} existingBid={existingBid} onTaken={onTaken} />
          {!mobile && matches.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <MatchList title="Passer etter dette" matches={matches} onPick={onPickMatch} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function RouteLines({ pickup, delivery, pickupLogo }) {
  return (
    <div style={{ display: 'flex', gap: 9, marginBottom: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 2, flexShrink: 0 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', border: `2px solid ${COLORS.success}`, background: COLORS.white }} />
        <div style={{ width: 1.5, flex: 1, background: COLORS.border, minHeight: 12 }} />
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.danger }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, flex: 1, minWidth: 0 }}>
        <span style={styles.address}>{pickupLogo}{pickup}</span>
        <span style={styles.address}>{delivery}</span>
      </div>
    </div>
  );
}

// Ett forslag i «Passer etter dette»: trykk velger oppdraget i «Nye oppdrag».
function MatchRow({ match, onPick }) {
  const { order } = match;
  const time = timeLabel(toDate(order.scheduledAt));
  return (
    <button
      onClick={e => { e.stopPropagation(); onPick(order); }}
      style={styles.matchRow}
    >
      <MassIcon massType={order.massType} size={28} />
      <span style={styles.matchRoute}>
        {placeName(order.pickupAddress)} → {placeName(order.deliveryAddress)}
      </span>
      {time && <span style={styles.matchMeta}>kl. {time}</span>}
    </button>
  );
}

export function MatchList({ title, matches, onPick }) {
  if (matches.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      {title && <div style={styles.matchTitle}>{title}</div>}
      {matches.slice(0, 3).map(m => <MatchRow key={m.order.id} match={m} onPick={onPick} />)}
    </div>
  );
}

function SkeletonCards() {
  return (
    <>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ background: COLORS.white, borderRadius: 8, padding: '12px 14px', marginBottom: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <div className="skeleton" style={{ height: 18, width: '35%' }} />
            <div className="skeleton" style={{ height: 18, width: '28%' }} />
          </div>
          <div className="skeleton" style={{ height: 12, width: '72%', marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 12, width: '60%', marginBottom: 10 }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="skeleton" style={{ height: 11, width: '25%' }} />
            <div className="skeleton" style={{ height: 11, width: '20%' }} />
          </div>
        </div>
      ))}
    </>
  );
}

// variant 'row': «Oppdragsgiver» som en rad i detaljlista, trykk åpner profilen i en modal
export function OrdererChip({ ordererId, variant = 'chip' }) {
  const { profile, loading } = useOrdererProfile(ordererId);
  const [showModal, setShowModal] = useState(false);

  if (!ordererId || loading || !profile) return null;

  const badgeKey = computeOrdererBadge({ orderCount: profile.orderCount ?? 0, tippiVerified: profile.tippiVerified === true });
  const badge    = ORDERER_BADGE_CONFIG[badgeKey];

  const displayName = profile.companyName || profile.name || 'Bestiller';

  if (variant === 'row') {
    return (
      <>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setShowModal(true); }}
          style={styles.ordererRow}
          aria-haspopup="dialog"
          title="Se bestillerprofil"
        >
          <span>Oppdragsgiver</span>
          <span style={styles.ordererRowValue}>
            <span aria-hidden="true">{badge.icon}</span>
            <b style={styles.ordererRowName}>{displayName}</b>
            <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1, color: COLORS.textLight }}>›</span>
          </span>
        </button>
        {showModal && (
          <OrdererModal profile={profile} badge={badge} onClose={() => setShowModal(false)} />
        )}
      </>
    );
  }

  return (
    <>
      <div
        onClick={e => { e.stopPropagation(); setShowModal(true); }}
        style={styles.ordererChip}
        title="Se bestillerprofil"
      >
        <span style={{ fontSize: 13 }}>{badge.icon}</span>
        <span style={{ color: COLORS.textDark, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {profile.companyName || profile.name || 'Bestiller'}
        </span>
        {profile.companyName && profile.name && (
          <span style={{ color: COLORS.textLight, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            · {profile.name}
          </span>
        )}
        <span style={{ color: COLORS.textLight, fontSize: 11, marginLeft: 'auto' }}>→</span>
      </div>

      {showModal && (
        <OrdererModal
          profile={profile}
          badge={badge}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

function OrdererModal({ profile, badge, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={onClose}
    >
      <div
        style={{ background: COLORS.white, borderRadius: 12, padding: '24px', width: '100%', maxWidth: 340, boxShadow: SHADOWS.lg }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 28 }}>{badge.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textDark }}>{badge.label}</div>
              <div style={{ fontSize: 12, color: COLORS.textLight }}>{profile.orderCount ?? 0} fullførte oppdrag</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: COLORS.textLight, padding: 0, lineHeight: 1 }}>✕</button>
        </div>

        {profile.companyName && (
          <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textDark, marginBottom: 2 }}>{profile.companyName}</div>
        )}
        {profile.name && (
          <div style={{ fontSize: 14, color: COLORS.textMid, marginBottom: profile.bio ? 12 : 0 }}>{profile.name}</div>
        )}
        {profile.bio && (
          <p style={{ fontSize: 13, color: COLORS.textMid, margin: '12px 0 0', lineHeight: 1.5 }}>"{profile.bio}"</p>
        )}
      </div>
    </div>,
    document.body
  );
}

// Samme oppsett som OrderCard, men med status i stedet for pris, og det ene beste
// oppdraget som passer etter (samme dag) alltid synlig under.
function ActiveOrderCard({ order, selected, onSelect, openOrders = NO_ORDERS, onPickMatch, onOpen }) {
  const bestMatch = useMemo(() => bestMatchSameDay(order, openOrders), [order, openOrders]);

  const time    = timeLabel(toDate(order.scheduledAt));
  const details = [
    getMassSubLabel(order),
    order.estimatedTons != null && `${order.estimatedTons} tonn`,
    orderHourlyText(order),
  ].filter(Boolean);

  return (
    <div
      className="tippi-order-card"
      onClick={() => onSelect?.(selected ? null : order)}
      style={{ ...styles.card, ...(selected ? styles.cardSelected : {}) }}
    >
      <div style={styles.cardRow}>
        <MassIcon massType={order.massType} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.route}>
            <OrderSupplierLogo order={order} />{placeName(order.pickupAddress)} → {placeName(order.deliveryAddress)}
          </div>
          <div style={styles.cardSub}>
            {massLabel(order.massType)}{time && ` · kl. ${time}`}
          </div>
        </div>
        <div style={styles.statusText}>{STATUS_TEXT[order.status] ?? 'Aktivt'}</div>
      </div>

      {selected && (
        <div style={styles.cardDetails}>
          <RouteLines
            pickup={fullAddress(order.pickupAddress)}
            delivery={fullAddress(order.deliveryAddress)}
          />
          {details.length > 0 && <div style={styles.cardMeta}>{details.join(' · ')}</div>}
          <button
            className="tippi-btn"
            style={styles.goToOrderBtn}
            onClick={e => { e.stopPropagation(); onOpen?.(order); }}
          >
            Gå til oppdrag →
          </button>
        </div>
      )}

      {bestMatch && onPickMatch && (
        <div style={{ marginTop: 10 }}>
          <MatchList title="↪ Passer etter dette" matches={[bestMatch]} onPick={onPickMatch} />
        </div>
      )}
    </div>
  );
}

const styles = {
  sidebar:      { width: 320, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${COLORS.border}`, background: COLORS.bg, overflow: 'hidden' },
  sidebarMobile:{ width: '100%', borderRight: 'none', background: COLORS.white },
  filterBoxMobile: { padding: '6px 20px 4px', borderBottom: 'none' },
  listMobile:   { padding: '0 20px 16px', gap: 0, background: COLORS.white },
  cardFlat:     { borderRadius: 0, boxShadow: 'none', background: 'transparent', border: 'none', borderBottom: `1px solid ${COLORS.border}`, padding: '14px 0' },
  filterBox:    { padding: '14px 16px', borderBottom: `1px solid ${COLORS.border}`, background: COLORS.white },
  select:       { width: '100%', padding: '9px 10px', borderRadius: 7, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.white, color: COLORS.textDark },
  filterLabel:  { fontSize: 12, color: COLORS.textMid, fontWeight: 500 },
  list:         { flex: 1, overflowY: 'auto', padding: '8px', display: 'flex', flexDirection: 'column', gap: 6 },
  card:         { background: COLORS.white, borderRadius: 14, padding: '12px', cursor: 'pointer', boxShadow: SHADOWS.sm, border: '1.5px solid transparent' },
  cardSelected: { borderColor: COLORS.accent, boxShadow: SHADOWS.md },
  matchTitle:   { fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 6 },
  matchRow:     { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 8px', marginBottom: 4, borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.white, cursor: 'pointer', textAlign: 'left' },
  matchRoute:   { flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: COLORS.textDark, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  matchMeta:    { fontSize: 12, color: COLORS.textMid, whiteSpace: 'nowrap', flexShrink: 0 },
  statusText:   { fontSize: 13, fontWeight: 600, color: COLORS.successText, textAlign: 'right', flexShrink: 0, maxWidth: 110 },
  matchBadge:   { fontSize: 12, color: COLORS.successText, fontWeight: 600, marginTop: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  cardRow:      { display: 'flex', alignItems: 'center', gap: 10 },
  route:        { fontSize: 16, fontWeight: 700, color: COLORS.textDark, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  cardSub:      { fontSize: 13, color: COLORS.textMid, marginTop: 2 },
  cardDetails:  { borderTop: `1px solid ${COLORS.border}`, marginTop: 12, paddingTop: 12 },
  dayHeader:    { fontSize: 13, fontWeight: 600, color: COLORS.textMid, padding: '8px 4px 0' },
  price:        { fontFamily: FONTS.display, color: COLORS.textDark, fontWeight: 700, fontSize: 26, lineHeight: 1 },
  cardMono:     { fontFamily: 'var(--font-mono)', fontSize: 11, color: COLORS.textLight, marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.3 },
  sheetTitle:   { fontFamily: FONTS.display, fontWeight: 700, fontSize: 26, lineHeight: 1, color: COLORS.textDark },
  bidTag:       { fontFamily: FONTS.display, color: COLORS.textLight, fontWeight: 700, fontSize: 22, lineHeight: 1 },
  myBidLabel:   { fontSize: 11, fontWeight: 700, color: COLORS.warningText, marginTop: 2 },
  myBidAmount:  { color: COLORS.warning, fontWeight: 700, fontSize: 15 },
  myBidBanner:  { background: COLORS.blueBg, color: COLORS.blueText, borderRadius: 4, padding: '4px 8px', fontSize: 11, fontWeight: 600, marginBottom: 10 },
  address:      { fontSize: 14, color: COLORS.textDark, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 },
  cardMeta:     { fontSize: 13, color: COLORS.textMid, marginBottom: 10 },
  bidBtn:       { marginTop: 10, width: '100%', padding: '13px', borderRadius: 26, border: 'none', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  kindTag:      { fontFamily: 'var(--font-mono)', fontSize: 10, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 4, whiteSpace: 'nowrap' },
  takeMsg:      { fontSize: 13, marginTop: 8, textAlign: 'center' },
  stateErr:     { textAlign: 'center', color: COLORS.dangerText, padding: 20, fontSize: 14 },
  emptyState:   { textAlign: 'center', padding: '40px 16px', color: COLORS.textMid },
  count:        { padding: '8px 16px', borderTop: `1px solid ${COLORS.border}`, fontSize: 11, color: COLORS.textLight, background: COLORS.white, fontWeight: 500 },
  ordererChip: {
    display: 'flex', alignItems: 'center', gap: 5,
    background: COLORS.surfaceHover, border: `1px solid ${COLORS.border}`,
    borderRadius: 6, padding: '4px 8px', marginBottom: 8,
    cursor: 'pointer', userSelect: 'none',
  },
  ordererRow:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, width: '100%', padding: '13px 0', background: 'none', border: 'none', borderBottom: `1px solid ${COLORS.border}`, fontFamily: 'inherit', fontSize: 14, color: COLORS.textMid, cursor: 'pointer', textAlign: 'left' },
  ordererRowValue: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 },
  ordererRowName:  { color: COLORS.textDark, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  filterHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', width: '100%', cursor: 'pointer', padding: 0 },
  filterBadge:     { background: COLORS.primary, color: COLORS.onPrimary, borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px', minWidth: 18, textAlign: 'center' },
  toggleGroup:     { display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 },
  toggleBtn:       { padding: '5px 10px', borderRadius: 6, border: `1.5px solid ${COLORS.border}`, background: COLORS.white, fontSize: 12, cursor: 'pointer', color: COLORS.textMid, fontWeight: 500 },
  toggleBtnActive: { borderColor: COLORS.primary, background: COLORS.primary + '15', color: COLORS.primary, fontWeight: 700 },
  resetBtn:        { padding: '8px', borderRadius: 6, border: `1px solid ${COLORS.border}`, background: COLORS.white, fontSize: 12, color: COLORS.textMid, cursor: 'pointer', textAlign: 'center' },
  activeSection:  { padding: '10px 8px 4px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', flexDirection: 'column', gap: 8 },
  sectionHeader:  { fontSize: 10, fontWeight: 700, color: COLORS.textLight, letterSpacing: 0.8, textTransform: 'uppercase', display: 'block', marginBottom: 6, paddingLeft: 2 },
  peekMeta:     { fontSize: 13, color: COLORS.textMid, lineHeight: 1.4, marginBottom: 2 },
  peekBtn:      { flex: 1, minWidth: 0, padding: '13px 8px', borderRadius: 26, fontWeight: 600, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  peekBtnPrimary: { border: 'none', background: COLORS.primary, color: COLORS.onPrimary },
  peekBtnOutline: { border: `2px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark },
  peekLink:     { display: 'block', margin: '8px auto 0', padding: '4px 8px', background: 'none', border: 'none', color: COLORS.textMid, fontSize: 13, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' },
  goToOrderBtn: { width: '100%', padding: '13px', borderRadius: 26, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
};
